import { format } from 'date-fns'

import { formatTimezoneTime, type AppTimezonePreference } from '@sparkai/utils'

import { type BaseNotificationItemProps, NotificationItem } from '../components/NotificationItem'

export type OperatorShiftCanceledReason =
  | 'NO_SHOW'
  | 'MID_SHIFT_DISCONNECTION'
  | 'NO_TASK_ASSIGNED_IN_PERIOD'
  | 'AFK_OR_OFFLINE'

export type OperatorShiftCanceledProps = BaseNotificationItemProps & {
  originalShift: {
    start: string
    end: string
  }
  type: 'operator_shift_cancelled'
  reason?: OperatorShiftCanceledReason
}

const getOperatorReasonDetails = (
  reason?: OperatorShiftCanceledReason,
  shift?: { start: string; end: string },
  timezonePreference?: AppTimezonePreference
) => {
  const fmtDate = (isoString: string, includeTimezoneIndicator = false) =>
    timezonePreference
      ? formatTimezoneTime(isoString, timezonePreference, { includeTimezoneIndicator })
      : format(new Date(isoString), 'h:mm aaa')
  const timeRange = shift ? `from ${fmtDate(shift.start)} to ${fmtDate(shift.end, true)}` : ''

  switch (reason) {
    case 'NO_SHOW':
      return {
        title: 'Your Shift Has Been Canceled Due To No Show',
        description: `Your shift ${timeRange} has been canceled because you did not check in within 5 minutes of the start time.`,
      }
    case 'MID_SHIFT_DISCONNECTION':
    case 'AFK_OR_OFFLINE':
      return {
        title: 'Your Shift Has Been Canceled Due To Inactivity',
        description: `Your shift ${timeRange} has been canceled due to inactivity detected during your shift.`,
      }
    case 'NO_TASK_ASSIGNED_IN_PERIOD':
      return {
        title: 'Your Shift Has Been Canceled Due To No Task Assigned',
        description: `Your shift ${timeRange} has been canceled because no task was assigned within the expected timeframe.`,
      }
    default:
      return {
        title: 'Your Shift Has Been Canceled',
        description: `Your shift ${timeRange} has been canceled.`,
      }
  }
}

export function OperatorShiftCanceled({ reason, originalShift, ...props }: OperatorShiftCanceledProps) {
  const reasonDetails = getOperatorReasonDetails(reason, originalShift, props.timezonePreference)

  return (
    <NotificationItem
      {...props}
      title={reasonDetails.title}
      descriptionAddon={<p className='text-sm text-gray-500'>{reasonDetails.description}</p>}
    />
  )
}
